import {useRef, useCallback, useEffect, type ChangeEvent, type PointerEvent as ReactPointerEvent} from 'react'
import {Box, TextField, Slider, Typography} from '@mui/material'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── Constants ─────────────────────────────────────────────────────

const SPHERE_SIZE = 120
const SENSITIVITY = 1.5

// ── RotationSphere — 3D sphere that user drags to rotate ─────────

interface RotationSphereProps {
  rotationX: number
  rotationY: number
  rotationZ: number
  onChangeX: (v: number) => void
  onChangeY: (v: number) => void
  onChangeZ: (v: number) => void
  /** Called on mouseup / slider committed / input blur to seal the undo merge chain */
  onDragEnd?: () => void
}

const RotationSphere = ({
  rotationX,
  rotationY,
  rotationZ,
  onChangeX,
  onChangeY,
  onChangeZ,
  onDragEnd,
}: RotationSphereProps) => {
  const tokens = useAppTokens()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const draggingRef = useRef(false)
  const lastPosRef = useRef({x: 0, y: 0})
  const rotXRef = useRef(rotationX)
  const rotYRef = useRef(rotationY)

  useEffect(() => {
    rotXRef.current = rotationX
  }, [rotationX])
  useEffect(() => {
    rotYRef.current = rotationY
  }, [rotationY])

  const handlePointerDown = useCallback((e: ReactPointerEvent) => {
    draggingRef.current = true
    lastPosRef.current = {x: e.clientX, y: e.clientY}
  }, [])

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (!draggingRef.current) return
      const dx = e.clientX - lastPosRef.current.x
      const dy = e.clientY - lastPosRef.current.y
      lastPosRef.current = {x: e.clientX, y: e.clientY}

      const newY = Math.max(-180, Math.min(180, rotYRef.current + dx * SENSITIVITY))
      const newX = Math.max(-180, Math.min(180, rotXRef.current + dy * SENSITIVITY))
      onChangeY(Math.round(newY))
      onChangeX(Math.round(newX))
    }
    const handlePointerUp = () => {
      if (draggingRef.current) {
        draggingRef.current = false
        onDragEnd?.()
      }
    }
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [onChangeX, onChangeY, onDragEnd])

  // Draw the sphere that visually rotates with "Abc" text inside
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = SPHERE_SIZE * dpr
    canvas.height = SPHERE_SIZE * dpr
    ctx.scale(dpr, dpr)
    ctx.clearRect(0, 0, SPHERE_SIZE, SPHERE_SIZE)

    const cx = SPHERE_SIZE / 2
    const cy = SPHERE_SIZE / 2
    const r = SPHERE_SIZE / 2 - 4

    // Background circle
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.fillStyle = tokens.sidebar.background
    ctx.fill()
    ctx.strokeStyle = tokens.accordion.border
    ctx.lineWidth = 1
    ctx.stroke()

    // 3D sphere shading
    const gradient = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, r * 0.1, cx, cy, r)
    gradient.addColorStop(0, 'rgba(196, 181, 253, 0.18)')
    gradient.addColorStop(0.6, 'rgba(124, 92, 255, 0.1)')
    gradient.addColorStop(1, 'rgba(79, 61, 217, 0.22)')
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.fillStyle = gradient
    ctx.fill()

    const radX = (rotationX * Math.PI) / 180
    const radY = (rotationY * Math.PI) / 180

    // Clip to sphere
    ctx.save()
    ctx.beginPath()
    ctx.arc(cx, cy, r - 1, 0, Math.PI * 2)
    ctx.clip()

    // Helper: project a 3D point on unit sphere to 2D with rotation
    const project = (sx: number, sy: number, sz: number): {px: number; py: number; depth: number} => {
      // Apply X rotation
      const y1 = sy * Math.cos(radX) - sz * Math.sin(radX)
      const z1 = sy * Math.sin(radX) + sz * Math.cos(radX)
      // Apply Y rotation
      const x2 = sx * Math.cos(radY) + z1 * Math.sin(radY)
      const z2 = -sx * Math.sin(radY) + z1 * Math.cos(radY)
      return {px: cx + x2 * r, py: cy - y1 * r, depth: z2}
    }

    // Draw longitude lines
    const lonCount = 8
    for (let i = 0; i < lonCount; i++) {
      const lonAngle = (i * Math.PI) / lonCount
      ctx.beginPath()
      let started = false
      for (let j = 0; j <= 64; j++) {
        const t = (j / 64) * Math.PI * 2
        const sx = Math.sin(lonAngle) * Math.cos(t)
        const sy = Math.sin(t)
        const sz = Math.cos(lonAngle) * Math.cos(t)
        const {px, py, depth} = project(sx, sy, sz)
        ctx.strokeStyle = depth >= 0 ? 'rgba(167, 139, 250, 0.4)' : 'rgba(167, 139, 250, 0.1)'
        ctx.lineWidth = 0.8
        if (!started) {
          ctx.moveTo(px, py)
          started = true
        } else {
          ctx.lineTo(px, py)
        }
      }
      ctx.stroke()
    }

    // Draw latitude lines
    const latCount = 7
    for (let i = 1; i < latCount; i++) {
      const latAngle = (i * Math.PI) / latCount - Math.PI / 2
      const rLat = Math.cos(latAngle)
      const yLat = Math.sin(latAngle)
      ctx.beginPath()
      let started = false
      for (let j = 0; j <= 64; j++) {
        const t = (j / 64) * Math.PI * 2
        const sx = rLat * Math.sin(t)
        const sy = yLat
        const sz = rLat * Math.cos(t)
        const {px, py, depth} = project(sx, sy, sz)
        ctx.strokeStyle = depth >= 0 ? 'rgba(167, 139, 250, 0.4)' : 'rgba(167, 139, 250, 0.1)'
        ctx.lineWidth = 0.8
        if (!started) {
          ctx.moveTo(px, py)
          started = true
        } else {
          ctx.lineTo(px, py)
        }
      }
      ctx.stroke()
    }

    ctx.restore()

    // "Abc" text in sphere — true 3D projection via affine transform
    const origin = project(0, 0, 0)
    const right = project(1, 0, 0)
    const up = project(0, 1, 0)

    // Basis vectors normalized by r for 1:1 screen-pixel mapping
    // Negate up-vector because canvas Y axis points downward
    const rx = (right.px - origin.px) / r
    const ry = (right.py - origin.py) / r
    const ux = -(up.px - origin.px) / r
    const uy = -(up.py - origin.py) / r

    ctx.save()
    ctx.beginPath()
    ctx.arc(cx, cy, r - 1, 0, Math.PI * 2)
    ctx.clip()
    // setTransform in device pixels
    ctx.setTransform(rx * dpr, ry * dpr, ux * dpr, uy * dpr, origin.px * dpr, origin.py * dpr)
    ctx.font = 'bold 14px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    // Stroke for 3D depth/thickness
    ctx.lineWidth = 2
    ctx.strokeStyle = 'rgba(79, 61, 217, 0.6)'
    ctx.strokeText('Abc', 0, 0)
    ctx.fillStyle = 'rgba(196, 181, 253, 0.85)'
    ctx.fillText('Abc', 0, 0)
    ctx.restore()
  }, [rotationX, rotationY, tokens])

  const handleInputX = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value)
      if (!Number.isNaN(val)) onChangeX(Math.max(-180, Math.min(180, val)))
    },
    [onChangeX],
  )

  const handleInputY = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value)
      if (!Number.isNaN(val)) onChangeY(Math.max(-180, Math.min(180, val)))
    },
    [onChangeY],
  )

  const handleInputZ = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value)
      if (!Number.isNaN(val)) onChangeZ(Math.max(-180, Math.min(180, val)))
    },
    [onChangeZ],
  )

  const handleSliderZ = useCallback(
    (_: Event, value: number | number[]) => {
      onChangeZ(value as number)
    },
    [onChangeZ],
  )

  const sx = buildStyles(tokens)

  return (
    <Box sx={sx.wrapper}>
      <Box sx={sx.sphereWithSlider}>
        <canvas
          ref={canvasRef}
          style={{width: SPHERE_SIZE, height: SPHERE_SIZE, cursor: 'grab', touchAction: 'none'}}
          onPointerDown={handlePointerDown}
        />
        <Box sx={sx.verticalSliderBox}>
          <Typography sx={sx.verticalSliderLabel}>Z</Typography>
          <Slider
            value={rotationZ}
            min={-180}
            max={180}
            step={1}
            orientation="vertical"
            onChange={handleSliderZ}
            onChangeCommitted={() => onDragEnd?.()}
            sx={sx.verticalSlider}
            size="small"
          />
          <Typography sx={sx.verticalSliderValue}>{rotationZ}°</Typography>
        </Box>
      </Box>
      <Box sx={sx.inputs}>
        <Box sx={sx.inputRow}>
          <Box component="span" sx={sx.inputLabel}>
            X
          </Box>
          <TextField
            value={rotationX}
            onChange={handleInputX}
            onBlur={onDragEnd}
            size="small"
            type="number"
            sx={sx.numberInput}
          />
          <Box component="span" sx={sx.unitLabel}>
            °
          </Box>
        </Box>
        <Box sx={sx.inputRow}>
          <Box component="span" sx={sx.inputLabel}>
            Y
          </Box>
          <TextField
            value={rotationY}
            onChange={handleInputY}
            onBlur={onDragEnd}
            size="small"
            type="number"
            sx={sx.numberInput}
          />
          <Box component="span" sx={sx.unitLabel}>
            °
          </Box>
        </Box>
        <Box sx={sx.inputRow}>
          <Box component="span" sx={sx.inputLabel}>
            Z
          </Box>
          <TextField
            value={rotationZ}
            onChange={handleInputZ}
            onBlur={onDragEnd}
            size="small"
            type="number"
            sx={sx.numberInput}
          />
          <Box component="span" sx={sx.unitLabel}>
            °
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    alignItems: 'center',
  },
  sphereWithSlider: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: '8px',
  },
  verticalSliderBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '2px',
    minWidth: 24,
  },
  verticalSliderLabel: {
    fontSize: 9,
    fontWeight: 600,
    color: tokens.text.muted,
  },
  verticalSlider: {
    flex: 1,
    '& .MuiSlider-thumb': {width: 10, height: 10},
    '& .MuiSlider-rail': {opacity: 0.3},
  },
  verticalSliderValue: {
    fontSize: 9,
    fontWeight: 500,
    color: tokens.text.secondary,
  },
  inputs: {
    display: 'flex',
    gap: '6px',
    width: '100%',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    flex: 1,
  },
  inputLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: tokens.text.muted,
    minWidth: 12,
  },
  unitLabel: {
    fontSize: 11,
    color: tokens.text.muted,
  },
  numberInput: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      fontSize: 12,
      color: tokens.text.primary,
    },
    '& .MuiOutlinedInput-input': {
      padding: '4px 6px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.accordion.border,
    },
  },
})

export default RotationSphere
